import { toMilliseconds } from '@art-suite/art-core-ts';
import { CacheEntry, PersistenceLayerBase, TypedCacheEntry } from '@kaiizen/react-persistent-cache';
import { DBSchema, IDBPCursorWithValueIteratorValue, IDBPDatabase, openDB } from 'idb';

const DEFAULT_DB_PREFIX = 'react-persistent-cache';
const DEFAULT_STORE_NAME = 'react-persistent-cache-entries';
const DB_VERSION = 1;
const LOG_PREFIX = 'IndexedDbPersistenceLayer';

const createPrefixUpperBound = (prefix: string): string => {
  if (prefix.length === 0) return '\uFFFF';

  const lastCharIndex = prefix.length - 1;
  const lastCharCode = prefix.charCodeAt(lastCharIndex);

  if (lastCharCode < 0xffff) return prefix.slice(0, lastCharIndex) + String.fromCharCode(lastCharCode + 1);
  if (prefix.length === 1) return '\uFFFF';
  return createPrefixUpperBound(prefix.slice(0, -1));
};

const createPrefixRange = (prefix: string): IDBKeyRange => {
  if (prefix.length === 0) return IDBKeyRange.lowerBound('');
  const upperBound = createPrefixUpperBound(prefix);
  if (upperBound === prefix) return IDBKeyRange.lowerBound(prefix);
  return IDBKeyRange.bound(prefix, upperBound, false, true);
};

const getNow = () => Date.now();
const isExpired = (e: CacheEntry, now = getNow()) => e.expiresAt <= now;
const randomBackoff = () => new Promise<void>(resolve => setTimeout(resolve, 50 + Math.random() * 150));

type PersistentCacheSchema<ValueType> = DBSchema & {
  entries: {
    key: string;
    value: TypedCacheEntry<ValueType>;
  };
};

export class IndexedDbPersistenceLayer<ValueType = unknown> extends PersistenceLayerBase<ValueType> {
  private dbPromise: Promise<IDBPDatabase<PersistentCacheSchema<ValueType>>> | undefined;
  readonly crossWindowChannelName: string | undefined;

  // storeName is preserved as the caller-facing "namespace".
  // We isolate per instance by default by embedding storeName into the DB name.
  // syncAcrossWindows: false keeps each window's PersistentCache over this database from hearing the others' writes.
  constructor(
    private storeName: string = DEFAULT_STORE_NAME,
    private dbNamePrefix: string = DEFAULT_DB_PREFIX,
    { syncAcrossWindows = true }: { syncAcrossWindows?: boolean } = {},
  ) {
    super();
    this.crossWindowChannelName = syncAcrossWindows ? this.getDbName() : undefined;
  }

  async set(key: string, value: ValueType, expiresAt?: number, createdAt?: number, now?: number): Promise<TypedCacheEntry<ValueType>> {
    const entry: TypedCacheEntry<ValueType> = {
      key,
      value,
      expiresAt: this.getExpiresAt(expiresAt, now),
      createdAt: createdAt ?? toMilliseconds(now),
    };

    await this.withDb(async db => {
      await db.put('entries', entry, key);
    });
    return entry;
  }

  async getEntry(key: string, now?: number, prefetchOnly?: boolean) {
    return this.withDb(async db => {
      const entry = await this.getValidEntry(db, key, now);
      if (!entry) return undefined;
      return prefetchOnly ? { key, value: undefined, createdAt: entry.createdAt, expiresAt: entry.expiresAt } : entry;
    });
  }

  async getMetadata(key: string, now?: number) {
    return this.withDb(async db => {
      const entry = await this.getValidEntry(db, key, now);
      return entry ? { key, value: undefined, createdAt: entry.createdAt, expiresAt: entry.expiresAt } : undefined;
    });
  }

  async has(key: string, now?: number) {
    return !!(await this.getEntry(key, now));
  }

  async purge(key: string): Promise<boolean> {
    return this.withDb(async db => {
      const entry = await db.get('entries', key);
      if (!entry) return false;
      await db.delete('entries', key);
      return true;
    });
  }

  async getAllEntries(prefix: string | undefined, now?: number): Promise<TypedCacheEntry<ValueType>[]> {
    return this.getAllEntriesInRange(prefix, now);
  }

  async getAllMetadata(prefix: string | undefined, now?: number) {
    const entries = await this.getAllEntriesInRange(prefix, now);
    return entries.map(e => ({ key: e.key, value: undefined, createdAt: e.createdAt, expiresAt: e.expiresAt }));
  }

  async getAllKeys(prefix: string | undefined, now?: number) {
    const entries = await this.getAllEntriesInRange(prefix, now);
    return entries.map(e => e.key);
  }

  // Freshening reads and rewrites each entry in one transaction, so it can't put back a value that
  // another window wrote in between.
  async freshen(key: string, expiresAt?: number, now?: number) {
    return this.withDb(async db => {
      const transaction = db.transaction('entries', 'readwrite');
      const entry = await transaction.store.get(key);
      const freshened = !!entry && !isExpired(entry, now);
      if (freshened) await transaction.store.put({ ...entry, expiresAt: this.getExpiresAt(expiresAt, now) }, key);
      await transaction.done;
      return freshened;
    });
  }

  async freshenAll(expiresAt: number | undefined, prefix: string | undefined, now?: number) {
    const resolvedExpiresAt = this.getExpiresAt(expiresAt, now);
    let count = 0;
    await this.forEachEntryReadwrite(prefix, async (cursor, entry) => {
      if (isExpired(entry, now)) return;
      await cursor.update({ ...cursor.value, expiresAt: resolvedExpiresAt });
      count++;
    });
    return count;
  }

  async purgeAllEntries(prefix: string | undefined, now?: number) {
    if (!prefix) {
      return this.withDb(async db => {
        const deletedCount = await db.count('entries');
        await db.clear('entries');
        return deletedCount;
      });
    }

    let count = 0;
    await this.forEachEntryReadwrite(prefix, async cursor => {
      await cursor.delete();
      count++;
    });
    return count;
  }

  async purgeExpiredEntries(now?: number) {
    let count = 0;
    await this.forEachEntryReadwrite(undefined, async (cursor, entry) => {
      if (isExpired(entry, now)) {
        await cursor.delete();
        count++;
      }
    });
    return count;
  }

  async getCacheCount() {
    return this.withDb(db => db.count('entries'));
  }

  //////////////////////////////////////////////////////////////////////////////////////////////
  // Private
  //////////////////////////////////////////////////////////////////////////////////////////////

  private getDbName() {
    // isolate per instance by default:
    // DB name is derived from storeName (namespace), store name inside DB is fixed ("entries").
    return `${this.dbNamePrefix}:${this.storeName}`;
  }

  private async getDb() {
    if (this.dbPromise) return this.dbPromise;

    const dbName = this.getDbName();

    this.dbPromise = openDB<PersistentCacheSchema<ValueType>>(dbName, DB_VERSION, {
      upgrade: db => {
        if (!db.objectStoreNames.contains('entries')) db.createObjectStore('entries');
      },
      blocked: () => {
        console.warn(`${LOG_PREFIX}: openDB blocked - another connection is preventing an upgrade`);
      },
      blocking: () => {
        // Close old connections so an upgrade elsewhere can proceed.
        // Clear dbPromise AFTER close to avoid a race where a concurrent getDb()
        // starts a new openDB() while the old connection is still in close-pending state.
        console.warn(`${LOG_PREFIX}: connection is blocking another tab's upgrade - closing`);
        this.dbPromise?.then(db => {
          db.close();
          this.dbPromise = undefined;
        });
      },
      terminated: () => {
        // The browser killed the connection (background tab, memory pressure, etc.).
        // Clear the cached promise so the next operation opens a fresh connection.
        console.warn(`${LOG_PREFIX}: connection terminated by browser`);
        this.dbPromise = undefined;
      },
    }).catch(error => {
      console.warn(`${LOG_PREFIX}: openDB failed`, error);
      this.dbPromise = undefined;
      throw error;
    });

    return this.dbPromise;
  }

  /**
   * Read entry by key. If expired, atomically delete it (re-checking in a readwrite
   * transaction to avoid racing with another tab that may have freshened the entry).
   */
  private async getValidEntry(
    db: IDBPDatabase<PersistentCacheSchema<ValueType>>,
    key: string,
    now?: number,
  ): Promise<TypedCacheEntry<ValueType> | undefined> {
    const entry = await db.get('entries', key);
    if (!entry) return undefined;
    if (!isExpired(entry, now)) return entry;

    const tx = db.transaction('entries', 'readwrite');
    const current = await tx.store.get(key);
    if (current && isExpired(current, now)) await tx.store.delete(key);
    await tx.done;
    return undefined;
  }

  /**
   * Runs an operation against the DB with automatic recovery:
   * - InvalidStateError (connection killed): backoff + reconnect + retry once
   * - QuotaExceededError (storage full): purge expired entries + retry once
   */
  private async withDb<T>(operation: (db: IDBPDatabase<PersistentCacheSchema<ValueType>>) => Promise<T>): Promise<T> {
    const db = await this.getDb();
    try {
      return await operation(db);
    } catch (error) {
      if (error instanceof DOMException) {
        if (error.name === 'InvalidStateError') {
          console.warn(`${LOG_PREFIX}: InvalidStateError - reconnecting after backoff`);
          this.dbPromise = undefined;
          await randomBackoff();
          const freshDb = await this.getDb();
          return await operation(freshDb);
        }
        if (error.name === 'QuotaExceededError') {
          console.warn(`${LOG_PREFIX}: QuotaExceededError - purging expired entries and retrying`);
          await this.purgeExpiredEntriesDirect(db);
          return await operation(db);
        }
      }
      throw error;
    }
  }

  /** Purge expired entries directly on a db handle (avoids recursion through withDb). */
  private async purgeExpiredEntriesDirect(db: IDBPDatabase<PersistentCacheSchema<ValueType>>) {
    const tx = db.transaction('entries', 'readwrite');
    const now = getNow();
    for await (const cursor of tx.store.iterate()) {
      if (isExpired(cursor.value as CacheEntry, now)) await cursor.delete();
    }
    await tx.done;
  }

  private async forEachEntryReadonly(
    prefix: string | undefined,
    // Callback must be synchronous - awaiting non-IDB work inside a cursor iteration
    // can cause the IDB transaction to auto-commit before iteration is complete.
    callback: (entry: CacheEntry) => void,
  ) {
    await this.withDb(async db => {
      const transaction = db.transaction('entries', 'readonly');
      const store = transaction.store;
      const range = prefix ? createPrefixRange(prefix) : undefined;

      for await (const cursor of store.iterate(range)) {
        const entry = cursor.value as CacheEntry;
        if (prefix && !entry.key.startsWith(prefix)) break;
        callback(entry);
      }

      await transaction.done;
    });
  }

  private async forEachEntryReadwrite(
    prefix: string | undefined,
    // Callback may only await IDB cursor operations (cursor.delete(), cursor.update(), etc.).
    // Awaiting non-IDB async work here can auto-commit the transaction before iteration completes.
    callback: (
      cursor: IDBPCursorWithValueIteratorValue<PersistentCacheSchema<ValueType>, ['entries'], 'entries', unknown, 'readwrite'>,
      entry: CacheEntry,
    ) => void | Promise<void>,
  ) {
    await this.withDb(async db => {
      const transaction = db.transaction('entries', 'readwrite');
      const store = transaction.store;
      const range = prefix ? createPrefixRange(prefix) : undefined;

      for await (const cursor of store.iterate(range)) {
        const entry = cursor.value as CacheEntry;
        if (prefix && !entry.key.startsWith(prefix)) break;
        await callback(cursor, entry);
      }

      await transaction.done;
    });
  }

  private async getAllEntriesInRange(prefix: string | undefined, now?: number): Promise<TypedCacheEntry<ValueType>[]> {
    const results: TypedCacheEntry<ValueType>[] = [];
    await this.forEachEntryReadonly(prefix, entry => {
      if (!isExpired(entry, now)) results.push(entry as TypedCacheEntry<ValueType>);
    });
    return results;
  }
}
